import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

const LanguageSelector = ({ variant = "default" }) => {
  const { lang, changeLang, FLAGS, LANGS, translating } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isCompact = variant === "compact";

  return (
    <div ref={ref} className="relative" data-testid="language-selector">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 transition-all duration-200 ${
          isCompact
            ? "text-lg px-1 py-1 rounded-full hover:bg-white/10"
            : "text-sm font-medium px-2.5 py-1.5 rounded-xl border border-zinc-200 hover:border-zinc-400 bg-white/80 backdrop-blur-sm"
        }`}
        data-testid="language-toggle-btn"
      >
        <span className={`relative inline-flex items-center justify-center ${isCompact ? "text-lg w-[1.35rem] h-[1.35rem]" : "text-base w-5 h-5"}`}>
          <AnimatePresence mode="wait" initial={false}>
            {translating ? (
              <motion.span
                key="spinner"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.18 }}
                className="absolute inset-0 flex items-center justify-center"
                data-testid="lang-switch-spinner"
                aria-label="Traduzione in corso"
              >
                <span className="block w-4 h-4 rounded-full border-2 border-zinc-300 border-t-zinc-800 animate-spin" />
              </motion.span>
            ) : (
              <motion.span
                key="flag"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.18 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                {FLAGS[lang].flag}
              </motion.span>
            )}
          </AnimatePresence>
        </span>
        {!isCompact && <span className="text-xs text-zinc-600 font-semibold">{lang.toUpperCase()}</span>}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-xl border border-zinc-100 overflow-hidden z-[100] min-w-[150px]"
            data-testid="language-dropdown"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => { changeLang(l); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
                  lang === l ? "bg-zinc-50 font-bold text-zinc-900" : "text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid={`lang-option-${l}`}
              >
                <span className="text-base">{FLAGS[l].flag}</span>
                <span>{FLAGS[l].label}</span>
                {lang === l && <span className="ml-auto text-zinc-400 text-xs">✓</span>}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
